import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from '@pagespeedify/ui';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    CircleDot,
    Expand,
    FileCode,
    Loader2,
    Minimize2,
    PanelLeft,
    Terminal,
    XCircle
} from 'lucide-react';
import { CodePanel, TerminalOutput } from '../json-render';
import type {
    AgentBatchPlanTreeBatch,
    AgentBatchPlanTreePlan,
    AgentBatchPlanTreeStep,
    AgentModifiedFilesSyncWarning
} from '../../types/agent';
import { defaultGeneratingBatchCopy, replaceGenericBatchCopy } from '../../lib/batchBootstrapCopy';

interface WorkspacePanelProps {
    terminalOutput: string[];
    fileChanges: {
        name: string;
        path: string;
        status: 'added' | 'modified' | 'deleted';
        diff?: string;
        contentPreview?: string;
    }[];
    modifiedFilesSyncWarning?: AgentModifiedFilesSyncWarning | null;
    filesList?: string[];
    activeTab?: string;
    onFileSelect?: (path: string) => void;
    fileContents?: Record<string, string>;
    isExpanded?: boolean;
    onToggleExpand?: () => void;
    viewMode?: 'tabbed' | 'stacked';
    onViewModeChange?: (mode: 'tabbed' | 'stacked') => void;
    batchPlanTree?: AgentBatchPlanTreeBatch[];
    executionMode?: 'review' | 'auto';
    isExecutionRunning?: boolean;
    isPlanExecuting?: boolean;
    onStartPlan?: (batchId: string, plan: AgentBatchPlanTreePlan) => void;
    canStartPlan?: boolean;
    onFindMoreIssuesForBatch?: (batchId: string) => void;
    onChangeGoalForBatch?: (batchId: string, goal: string) => void;
    isFindMoreIssuesActionAvailableForBatch?: (batchId: string) => boolean;
    isChangeGoalActionAvailableForBatch?: (batchId: string) => boolean;
    onActiveTabChange?: (tab: WorkspaceTab) => void;
    activeBatchId?: string | null;
    activePackageIndex?: number | null;
    activePlanVersion?: number | null;
    currentStatus?: string | null;
    executionStatus?: string | null;
    forceAutoFollowSignal?: number;
    taskGoal?: string | null;
    taskGoalLabel?: string | null;
}

type WorkspaceTab = 'files' | 'plans' | 'terminal';
type PlansGroupingMode = 'batch' | 'goal';
type PlanGoalGroup = {
    key: string;
    label: string;
    plans: AgentBatchPlanTreePlan[];
};

const PLANS_GROUPING_STORAGE_KEY = 'swayashin.plans.grouping';

type StatusMeta = {
    label: string;
    className: string;
    icon: typeof CheckCircle2;
    spin?: boolean;
};

const normalizeTab = (raw: string | undefined): WorkspaceTab => {
    const value = String(raw || '').trim().toLowerCase();
    if (value === 'plan' || value === 'plans') return 'plans';
    if (value === 'terminal') return 'terminal';
    return 'files';
};

const PLAN_DISPLAY_WORKER_ACTIVE_STATUSES = new Set([
    'approved',
    'plan_approved',
    'queued',
    'executing',
    'cloning',
    'analyzing',
    'generating_plan',
    'regenerating_plan',
    'creating_pr',
    'publishing_pr',
    'finalizing',
    'checking_pr_eligibility',
    'running_verification',
    'verifying',
    'verification_running',
]);

const PLAN_DISPLAY_WORKER_ACTIVE_TASK_STATUSES = new Set([
    'queued',
    'initializing',
    'cloning',
    'analyzing',
    'generating_plan',
    'regenerating_plan',
    'creating_pr',
    'publishing_pr',
    'finalizing',
    'checking_pr_eligibility',
    'running_verification',
    'verifying',
    'verification_running',
    'executing',
    'pausing',
]);

// Statuses where the worker is generating a NEW plan — not executing the current one.
// During these phases, the existing plan card badge must NOT be overridden to IN_PROGRESS.
const PLAN_GENERATION_ONLY_STATUSES = new Set([
    'generating_plan',
    'regenerating_plan',
]);

const isWorkerActiveForPlanDisplay = (params: {
    currentStatus: string | null;
    executionStatus: string | null;
    isExecutionRunning: boolean;
    isPlanExecuting?: boolean;
}): boolean => {
    if (params.isPlanExecuting !== undefined) {
        return params.isPlanExecuting;
    }
    if (params.isExecutionRunning) return true;
    const normalizedExecutionStatus = String(params.executionStatus || '').trim().toLowerCase();
    if (PLAN_DISPLAY_WORKER_ACTIVE_STATUSES.has(normalizedExecutionStatus)) return true;
    const normalizedCurrentStatus = String(params.currentStatus || '').trim().toLowerCase();
    return PLAN_DISPLAY_WORKER_ACTIVE_TASK_STATUSES.has(normalizedCurrentStatus);
};

const getStatusMeta = (status: string | undefined): StatusMeta => {
    const normalized = String(status || '').trim().toLowerCase();
    if (normalized === 'approved' || normalized === 'plan_approved') {
        return {
            label: 'APPROVED',
            className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
            icon: CheckCircle2
        };
    }
    if (normalized === 'completed') {
        return {
            label: 'COMPLETED',
            className: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
            icon: CheckCircle2
        };
    }
    if (normalized === 'failed') {
        return {
            label: 'FAILED',
            className: 'border-red-500/40 bg-red-500/15 text-red-300',
            icon: XCircle
        };
    }
    if (normalized === 'partial') {
        return {
            label: 'PARTIAL',
            className: 'border-amber-500/40 bg-amber-500/15 text-amber-300',
            icon: AlertTriangle
        };
    }
    if (normalized === 'blocked') {
        return {
            label: 'BLOCKED',
            className: 'border-amber-500/40 bg-amber-500/15 text-amber-300',
            icon: AlertTriangle
        };
    }
    if (normalized === 'skipped') {
        return {
            label: 'SKIPPED',
            className: 'border-amber-500/40 bg-amber-500/15 text-amber-300',
            icon: AlertTriangle
        };
    }
    if (normalized === 'in_progress' || normalized === 'in-progress' || normalized === 'running') {
        return {
            label: 'IN_PROGRESS',
            className: 'border-blue-500/40 bg-blue-500/15 text-blue-300',
            icon: Loader2,
            spin: true
        };
    }
    return {
        label: 'PENDING',
        className: 'border-zinc-500/40 bg-zinc-500/15 text-zinc-300',
        icon: CircleDot
    };
};

const buildPlanProgressText = (plan: AgentBatchPlanTreePlan): string => {
    const stepCount = Math.max(0, Number(plan.stepCount || 0));
    const completed = Math.max(0, Number(plan.completedSteps || 0));
    const failed = Math.max(0, Number(plan.failedSteps || 0));
    const blocked = Math.max(0, Number(plan.blockedSteps || 0));
    return `${completed}/${stepCount} completed${failed > 0 ? ` ï¿½ ${failed} failed` : ''}${blocked > 0 ? ` ï¿½ ${blocked} blocked` : ''}`;
};

const getLogicalPlanStatus = (plan: AgentBatchPlanTreePlan): string | undefined => {
    const steps = Array.isArray(plan.steps) ? plan.steps : [];
    const countByStatus = (status: string): number =>
        steps.filter((step) => String(step?.status || '').trim().toLowerCase() === status).length;
    const completed = Math.max(
        0,
        Number.isFinite(Number(plan.completedSteps)) ? Number(plan.completedSteps) : 0,
        countByStatus('completed')
    );
    const failed = Math.max(
        0,
        Number.isFinite(Number(plan.failedSteps)) ? Number(plan.failedSteps) : 0,
        countByStatus('failed')
    );
    const blocked = Math.max(
        0,
        Number.isFinite(Number(plan.blockedSteps)) ? Number(plan.blockedSteps) : 0,
        countByStatus('blocked')
    );
    const pending = countByStatus('pending');
    const running = countByStatus('running') + countByStatus('in_progress');
    const skipped = countByStatus('skipped');
    const stepCount = Math.max(
        0,
        Number.isFinite(Number(plan.stepCount)) ? Number(plan.stepCount) : 0,
        steps.length,
        completed + failed + blocked + pending + running + skipped
    );
    const normalizedStatus = String(plan.status || '').trim().toLowerCase();

    if (stepCount <= 0) return plan.status;
    if (running > 0) return 'in_progress';
    if (completed >= stepCount && failed === 0 && blocked === 0) return 'completed';
    if (completed > 0) return 'partial';
    if (blocked > 0 && failed === 0) return 'blocked';
    if (failed > 0) return 'failed';
    if (normalizedStatus === 'failed' && completed > 0) return 'partial';
    return plan.status;
};

const getDefaultBatchId = (batches: AgentBatchPlanTreeBatch[]): string | null => {
    if (!Array.isArray(batches) || batches.length === 0) return null;
    const activeBatch = batches.find((batch) => String(batch.status || '').toLowerCase() === 'active');
    if (activeBatch) return activeBatch.batchId;
    const withActivePlan = batches.find((batch) => Array.isArray(batch.plans) && batch.plans.some((plan) => plan?.isActive));
    if (withActivePlan) return withActivePlan.batchId;
    return batches[0]?.batchId || null;
};

const normalizeBatchIdentity = (raw: string | null | undefined): string => String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const isSameBatchIdentity = (left: string | null | undefined, right: string | null | undefined): boolean => {
    const normalizedLeft = normalizeBatchIdentity(left);
    const normalizedRight = normalizeBatchIdentity(right);
    return normalizedLeft.length > 0 && normalizedLeft === normalizedRight;
};

const findBatchByIdentity = (
    batches: AgentBatchPlanTreeBatch[],
    batchId: string | null | undefined
): AgentBatchPlanTreeBatch | null => {
    const normalizedBatchId = normalizeBatchIdentity(batchId);
    if (!normalizedBatchId) return null;
    return batches.find((batch) => normalizeBatchIdentity(batch.batchId) === normalizedBatchId) || null;
};

const isConcretePlan = (plan: AgentBatchPlanTreePlan | null | undefined): boolean => {
    if (!plan) return false;
    if (plan.isSynthetic === true || plan.isGenerating === true || plan.isPreparing === true) return false;
    return Array.isArray(plan.steps) && plan.steps.length > 0;
};

const isReviewablePlan = (plan: AgentBatchPlanTreePlan | null | undefined): boolean => {
    if (!isConcretePlan(plan)) return false;
    const normalizedStatus = String(plan?.status || '').trim().toLowerCase();
    return normalizedStatus !== 'completed'
        && normalizedStatus !== 'failed'
        && normalizedStatus !== 'skipped';
};

const hasBatchedPlanIdentity = (plan: AgentBatchPlanTreePlan | null | undefined): boolean =>
    !!plan && (
        (typeof plan.packageId === 'string' && plan.packageId.trim() !== '')
        || (typeof plan.batchId === 'string' && plan.batchId.trim() !== '')
        || (Number.isFinite(Number(plan.packageRank)) && Number(plan.packageRank) > 0)
    );

const getPlanOrdinal = (plan: AgentBatchPlanTreePlan | null | undefined): number | null => {
    if (!plan) return null;
    const packageRank = Number(plan.packageRank);
    if (Number.isFinite(packageRank) && packageRank > 0) {
        return Math.max(1, Math.floor(packageRank));
    }
    if (!hasBatchedPlanIdentity(plan)) {
        const version = Number(plan.version);
        if (Number.isFinite(version) && version > 0) {
            return Math.max(1, Math.floor(version));
        }
    }
    return null;
};

const getPlanIdentityKey = (plan: AgentBatchPlanTreePlan): string => {
    const goal = typeof plan.goal === 'string' && plan.goal.trim() !== ''
        ? plan.goal.trim().toLowerCase()
        : 'unassigned';
    const goalPrefix = `g:${goal}:`;
    const packageId = typeof plan.packageId === 'string' && plan.packageId.trim() !== ''
        ? plan.packageId.trim()
        : '';
    if (packageId) return `${goalPrefix}pkg:${packageId}`;
    const planOrdinal = getPlanOrdinal(plan);
    if (planOrdinal !== null) {
        return Number.isFinite(Number(plan.packageRank)) && Number(plan.packageRank) > 0
            ? `${goalPrefix}r:${planOrdinal}`
            : `${goalPrefix}v:${planOrdinal}`;
    }
    const summary = typeof plan.summary === 'string' ? plan.summary.trim().toLowerCase() : '';
    if (summary) return `${goalPrefix}s:${summary}`;
    const createdAt = typeof plan.createdAt === 'string' ? plan.createdAt.trim() : '';
    if (createdAt) return `${goalPrefix}c:${createdAt}`;
    return `${goalPrefix}fallback:0`;
};

const getPlanStepDisplayIndex = (
    step: AgentBatchPlanTreeStep,
    rowIndex: number
): number => {
    const candidate = Number((step as any)?.index);
    return Number.isFinite(candidate) && candidate > 0
        ? Math.floor(candidate)
        : rowIndex + 1;
};

const getPlanStepKey = (
    plan: AgentBatchPlanTreePlan,
    step: AgentBatchPlanTreeStep,
    rowIndex: number
): string => {
    const planKey = getPlanIdentityKey(plan);
    const stepId = typeof step.id === 'string' && step.id.trim() !== ''
        ? step.id.trim()
        : '';
    if (stepId) {
        return `plan-${planKey}-step-id:${stepId}`;
    }
    const title = typeof step.title === 'string' && step.title.trim() !== ''
        ? step.title.trim().toLowerCase()
        : 'untitled';
    return `plan-${planKey}-step-${getPlanStepDisplayIndex(step, rowIndex)}-${rowIndex}-${title}`;
};

const getPlanPriorityScore = (plan: AgentBatchPlanTreePlan): number => {
    let score = 0;
    if (plan.isSynthetic !== true) score += 10;
    if (isConcretePlan(plan)) score += 10;
    if (plan.isActive) score += 2;
    if (typeof plan.prUrl === 'string' && plan.prUrl.trim() !== '') score += 1;
    score += Math.max(0, Number(plan.completedSteps || 0)) * 2;
    score += Math.max(0, Number(plan.blockedSteps || 0));
    score += Math.max(0, Number(plan.failedSteps || 0));
    return score;
};

const dedupeBatchPlans = (batch: AgentBatchPlanTreeBatch): AgentBatchPlanTreePlan[] => {
    const rawPlans = Array.isArray(batch.plans)
        ? batch.plans.filter((plan): plan is AgentBatchPlanTreePlan => !!plan && typeof plan === 'object')
        : [];
    const deduped = new Map<string, AgentBatchPlanTreePlan>();
    for (const plan of rawPlans) {
        const key = getPlanIdentityKey(plan);
        const existing = deduped.get(key);
        if (!existing) {
            deduped.set(key, plan);
            continue;
        }
        const existingScore = getPlanPriorityScore(existing);
        const candidateScore = getPlanPriorityScore(plan);
        if (candidateScore > existingScore) {
            deduped.set(key, plan);
            continue;
        }
        if (candidateScore === existingScore) {
            const existingVersion = Number.isFinite(Number(existing.version)) ? Number(existing.version) : 0;
            const candidateVersion = Number.isFinite(Number(plan.version)) ? Number(plan.version) : 0;
            const existingCompleted = Math.max(0, Number(existing.completedSteps || 0));
            const candidateCompleted = Math.max(0, Number(plan.completedSteps || 0));
            if (
                candidateCompleted > existingCompleted
                || (
                    candidateCompleted === existingCompleted
                    && candidateVersion > existingVersion
                )
            ) {
                deduped.set(key, plan);
            }
        }
    }
    return Array.from(deduped.values()).sort((a, b) => {
        const rankA = getPlanOrdinal(a) ?? Number.MAX_SAFE_INTEGER;
        const rankB = getPlanOrdinal(b) ?? Number.MAX_SAFE_INTEGER;
        if (rankA !== rankB) return rankA - rankB;
        return Math.max(1, Number(a.version || 1)) - Math.max(1, Number(b.version || 1));
    });
};

const getPreferredPlanForBatch = (
    batch: AgentBatchPlanTreeBatch | null,
    options?: {
        preferredPlanKey?: string | null;
        authoritativePackageIndex?: number | null;
        authoritativePlanVersion?: number | null;
        preferReviewablePlan?: boolean;
    }
): AgentBatchPlanTreePlan | null => {
    if (!batch || !Array.isArray(batch.plans) || batch.plans.length === 0) return null;
    const plans = batch.plans;
    const preferredPlanKey = options?.preferredPlanKey ?? null;
    if (typeof preferredPlanKey === 'string' && preferredPlanKey.trim() !== '') {
        const exactMatch = plans.find((plan) => getPlanIdentityKey(plan) === preferredPlanKey) || null;
        if (exactMatch) return exactMatch;
    }
    const authoritativePlan = getAuthoritativePlanForBatch(
        batch,
        options?.authoritativePackageIndex,
        options?.authoritativePlanVersion
    );
    if (options?.preferReviewablePlan) {
        if (authoritativePlan && isReviewablePlan(authoritativePlan)) return authoritativePlan;
        const activeReviewable = plans.find((plan) => plan.isActive && isReviewablePlan(plan)) || null;
        if (activeReviewable) return activeReviewable;
        const firstReviewable = plans.find((plan) => isReviewablePlan(plan)) || null;
        if (firstReviewable) return firstReviewable;
    }
    if (authoritativePlan) return authoritativePlan;
    const activeConcrete = plans.find((plan) => plan.isActive && isConcretePlan(plan)) || null;
    if (activeConcrete) return activeConcrete;
    const firstConcrete = plans.find((plan) => isConcretePlan(plan)) || null;
    if (firstConcrete) return firstConcrete;
    const activeGenerating = plans.find((plan) => plan.isActive && (plan.isGenerating === true || plan.isPreparing === true)) || null;
    if (activeGenerating) return activeGenerating;
    const firstGenerating = plans.find((plan) => plan.isGenerating === true || plan.isPreparing === true) || null;
    if (firstGenerating) return firstGenerating;
    const firstWithPr = plans.find((plan) => typeof plan.prUrl === 'string' && plan.prUrl.trim() !== '') || null;
    if (firstWithPr) return firstWithPr;
    return plans[0] || null;
};

const getDisplayPlanNumber = (batch: AgentBatchPlanTreeBatch | null, plan: AgentBatchPlanTreePlan | null): number => {
  if (!batch || !Array.isArray(batch.plans) || !plan) return 1;
  const version = Number(plan.version);
  if (Number.isFinite(version) && version > 0) {
    return Math.max(1, Math.floor(version));
  }
  const concreteOrdinal = getPlanOrdinal(plan);
  if (concreteOrdinal !== null) {
    return concreteOrdinal;
  }
  const key = getPlanIdentityKey(plan);
  const index = batch.plans.findIndex((entry) => getPlanIdentityKey(entry) === key);
  return index >= 0 ? index + 1 : 1;
};

const batchHasMultiplePlanGoals = (
    batch: AgentBatchPlanTreeBatch | null,
    fallbackGoal?: string | null
): boolean => {
    if (!batch || !Array.isArray(batch.plans)) return false;
    const goals = new Set(batch.plans.map((plan) => getPlanGoalKey(plan, fallbackGoal)));
    return goals.size > 1;
};

const getDisplayPlanLabel = (
    batch: AgentBatchPlanTreeBatch | null,
    plan: AgentBatchPlanTreePlan | null,
    options?: {
        fallbackGoal?: string | null;
        fallbackGoalLabel?: string | null;
        forceGoalLabel?: boolean;
        suppressGoalLabel?: boolean;
        active?: boolean;
    }
): string => {
    const planNumber = getDisplayPlanNumber(batch, plan);
    if (!plan) return `Plan ${planNumber}`;
    const hasConcreteGoal =
        (typeof plan.goal === 'string' && plan.goal.trim() !== '')
        || (typeof plan.goalLabel === 'string' && plan.goalLabel.trim() !== '')
        || (typeof options?.fallbackGoal === 'string' && options.fallbackGoal.trim() !== '')
        || (typeof options?.fallbackGoalLabel === 'string' && options.fallbackGoalLabel.trim() !== '');
    const shouldShowGoal = options?.suppressGoalLabel === true
        ? false
        : (options?.forceGoalLabel === true && hasConcreteGoal)
        || batchHasMultiplePlanGoals(batch, options?.fallbackGoal);
    const goalLabel = shouldShowGoal
        ? getPlanGoalLabel(plan, options?.fallbackGoalLabel, options?.fallbackGoal)
        : '';
    const suffixParts = [
        ...(goalLabel ? [goalLabel] : []),
        ...(options?.active === true ? ['active'] : [])
    ];
    return suffixParts.length > 0
        ? `Plan ${planNumber} (${suffixParts.join(', ')})`
        : `Plan ${planNumber}`;
};

const getPlanGoalKey = (plan: AgentBatchPlanTreePlan, fallbackGoal?: string | null): string => {
    if (typeof plan.goal === 'string' && plan.goal.trim() !== '') {
        return plan.goal.trim().toLowerCase();
    }
    if (typeof fallbackGoal === 'string' && fallbackGoal.trim() !== '') {
        return fallbackGoal.trim().toLowerCase();
    }
    return 'unassigned';
};

const getPlanGoalLabel = (plan: AgentBatchPlanTreePlan, fallbackGoalLabel?: string | null, fallbackGoal?: string | null): string => {
    if (typeof plan.goalLabel === 'string' && plan.goalLabel.trim() !== '') {
        return plan.goalLabel.trim();
    }
    if (typeof plan.goal === 'string' && plan.goal.trim() !== '') {
        return plan.goal.trim().replace(/\b\w/g, (match) => match.toUpperCase());
    }
    if (typeof fallbackGoalLabel === 'string' && fallbackGoalLabel.trim() !== '') {
        return fallbackGoalLabel.trim();
    }
    if (typeof fallbackGoal === 'string' && fallbackGoal.trim() !== '') {
        return fallbackGoal.trim().replace(/\b\w/g, (match) => match.toUpperCase());
    }
    return 'Unassigned Goal';
};

const buildPlanGoalGroups = (plans: AgentBatchPlanTreePlan[], fallbackGoal?: string | null, fallbackGoalLabel?: string | null): PlanGoalGroup[] => {
    const groups = new Map<string, PlanGoalGroup>();
    for (const plan of plans) {
        const key = getPlanGoalKey(plan, fallbackGoal);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                label: getPlanGoalLabel(plan, fallbackGoalLabel, fallbackGoal),
                plans: []
            });
        }
        groups.get(key)!.plans.push(plan);
    }
    return Array.from(groups.values());
};

const getBatchFallbackGoal = (
    batch: AgentBatchPlanTreeBatch | null | undefined,
    taskGoal?: string | null
): string | null => {
    if (typeof batch?.latestGoal === 'string' && batch.latestGoal.trim() !== '') {
        return batch.latestGoal.trim();
    }
    if (typeof batch?.continuation?.currentGoal === 'string' && batch.continuation.currentGoal.trim() !== '') {
        return batch.continuation.currentGoal.trim();
    }
    if (typeof taskGoal === 'string' && taskGoal.trim() !== '') {
        return taskGoal.trim();
    }
    return null;
};

const getBatchFallbackGoalLabel = (
    batch: AgentBatchPlanTreeBatch | null | undefined,
    taskGoal?: string | null,
    taskGoalLabel?: string | null
): string | null => {
    if (typeof batch?.latestGoalLabel === 'string' && batch.latestGoalLabel.trim() !== '') {
        return batch.latestGoalLabel.trim();
    }
    if (typeof batch?.continuation?.currentGoalLabel === 'string' && batch.continuation.currentGoalLabel.trim() !== '') {
        return batch.continuation.currentGoalLabel.trim();
    }
    if (typeof taskGoalLabel === 'string' && taskGoalLabel.trim() !== '') {
        return taskGoalLabel.trim();
    }
    if (typeof taskGoal === 'string' && taskGoal.trim() !== '') {
        return taskGoal.trim().replace(/\b\w/g, (match) => match.toUpperCase());
    }
    return null;
};

const HorizontalScrollStrip = ({
    children,
    className = '',
    scrollerRef: externalScrollerRef,
    scrollerTestId
}: {
    children: React.ReactNode;
    className?: string;
    scrollerRef?: React.RefObject<HTMLDivElement | null>;
    scrollerTestId?: string;
}) => {
    const internalScrollerRef = useRef<HTMLDivElement | null>(null);
    const scrollerRef = externalScrollerRef || internalScrollerRef;
    const [overflowState, setOverflowState] = useState({
        hasOverflow: false,
        canScrollLeft: false,
        canScrollRight: false
    });

    const updateOverflowState = useCallback(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const hasOverflow = scroller.scrollWidth > scroller.clientWidth + 1;
        const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        setOverflowState({
            hasOverflow,
            canScrollLeft: hasOverflow && scroller.scrollLeft > 1,
            canScrollRight: hasOverflow && scroller.scrollLeft < maxScrollLeft - 1
        });
    }, [scrollerRef]);

    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const handleScroll = () => updateOverflowState();
        handleScroll();
        scroller.addEventListener('scroll', handleScroll, { passive: true });
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleScroll);
        }
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(handleScroll);
            resizeObserver.observe(scroller);
        }
        return () => {
            scroller.removeEventListener('scroll', handleScroll);
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleScroll);
            }
            resizeObserver?.disconnect();
        };
    }, [scrollerRef, updateOverflowState]);

    const scrollBy = (direction: 'left' | 'right') => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const delta = Math.max(120, Math.floor(scroller.clientWidth * 0.65));
        const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const nextScrollLeft = direction === 'left'
            ? Math.max(0, scroller.scrollLeft - delta)
            : Math.min(maxScrollLeft, scroller.scrollLeft + delta);
        scroller.scrollTo({
            left: nextScrollLeft,
            behavior: 'smooth'
        });
    };

    return (
        <div className={`flex items-center gap-1.5 ${className}`}>
            {overflowState.hasOverflow && (
                <button
                    type="button"
                    aria-label="Scroll batches left"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!overflowState.canScrollLeft}
                    onClick={() => scrollBy('left')}
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                </button>
            )}
            <div
                ref={scrollerRef}
                data-testid={scrollerTestId}
                className="flex-1 min-w-0 overflow-x-auto no-scrollbar"
            >
                <div className="flex min-w-max items-center gap-1.5">
                    {children}
                </div>
            </div>
            {overflowState.hasOverflow && (
                <button
                    type="button"
                    aria-label="Scroll batches right"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!overflowState.canScrollRight}
                    onClick={() => scrollBy('right')}
                >
                    <ChevronRight className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
};

const getPlanForVersion = (
    batch: AgentBatchPlanTreeBatch | null,
    planVersion: number | null | undefined
): AgentBatchPlanTreePlan | null => {
    if (!batch || !Array.isArray(batch.plans) || batch.plans.length === 0) return null;
    const normalizedVersion = Number(planVersion);
    if (!Number.isFinite(normalizedVersion) || normalizedVersion <= 0) return null;
    const versionedPlans = batch.plans.filter((plan) => {
        const version = Number(plan.version);
        return Number.isFinite(version) && version > 0 && Math.floor(version) === Math.floor(normalizedVersion);
    });
    return versionedPlans.find((plan) => plan.isActive) || versionedPlans[0] || null;
};

const getAuthoritativePlanForBatch = (
    batch: AgentBatchPlanTreeBatch | null,
    packageIndex: number | null | undefined,
    planVersion: number | null | undefined
): AgentBatchPlanTreePlan | null => {
    const versionedPlan = getPlanForVersion(batch, planVersion);
    if (versionedPlan) return versionedPlan;
    if (!batch || !Array.isArray(batch.plans) || batch.plans.length === 0) return null;
    const explicitlyActivePlan = batch.plans.find((plan) => plan.isActive);
    if (explicitlyActivePlan) return explicitlyActivePlan;
    const normalizedIndex = Number(packageIndex);
    if (Number.isFinite(normalizedIndex) && normalizedIndex >= 0) {
        return batch.plans[Math.floor(normalizedIndex)] || null;
    }
    return null;
};

const escapeAttributeValue = (value: string): string =>
    value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const findHorizontalScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
    let current = element?.parentElement || null;
    while (current) {
        const style = window.getComputedStyle(current);
        const allowsHorizontalScroll = style.overflowX === 'auto' || style.overflowX === 'scroll';
        if (allowsHorizontalScroll && current.scrollWidth > current.clientWidth + 1) {
            return current;
        }
        current = current.parentElement;
    }
    return null;
};

const canShowStartPlanAction = (params: {
    plan: AgentBatchPlanTreePlan | null;
    batch: AgentBatchPlanTreeBatch | null;
    executionMode: 'review' | 'auto';
}): boolean => {
    if (params.executionMode !== 'review') return false;
    const plan = params.plan;
    const batch = params.batch;
    if (!plan || !batch?.batchId) return false;
    if (String(batch.status || '').toLowerCase() !== 'active') return false;
    if (String(batch.bootstrapStatus || '').toLowerCase() !== 'ready') return false;
    if (!isConcretePlan(plan)) return false;
    if (plan.isActive === true || plan.isGenerating === true || plan.isPreparing === true) return false;
    const status = String(plan.status || '').trim().toLowerCase();
    return status === 'pending' || status === 'skipped';
};

const canShowBatchContinuationActions = (params: {
    batch: AgentBatchPlanTreeBatch | null;
    executionMode: 'review' | 'auto';
    isExecutionRunning: boolean;
    executionStatus: string | null;
}): boolean => {
    if (params.executionMode !== 'review') return false;
    if (params.isExecutionRunning) return false;
    if (!params.batch?.batchId) return false;
    const normalizedExecutionStatus = String(params.executionStatus || '').trim().toLowerCase();
    const blockedExecutionStatuses = new Set([
        'plan_pending_approval',
        'approved',
        'plan_approved',
        'executing',
        'analyzing',
        'generating_plan',
        'regenerating_plan',
        'cloning',
        'queued'
    ]);
    if (blockedExecutionStatuses.has(normalizedExecutionStatus)) return false;
    const normalizedBatchStatus = String(params.batch.status || '').trim().toLowerCase();
    const normalizedBatchPhase = String(params.batch.phase || '').trim().toLowerCase();
    const plans = Array.isArray(params.batch.plans) ? params.batch.plans : [];
    const exhaustedPlanStatuses = new Set(['completed', 'failed', 'blocked', 'partial']);
    const batchHasExhaustedPlans =
        plans.length > 0
        && plans.every((plan) => exhaustedPlanStatuses.has(String(plan?.status || '').trim().toLowerCase()));
    return (
        (
            normalizedBatchStatus === 'completed'
            || normalizedBatchPhase === 'batch_complete'
            || batchHasExhaustedPlans
        )
        && plans.length > 0
    );
};

export default function WorkspacePanel({
    terminalOutput,
    fileChanges,
    modifiedFilesSyncWarning = null,
    filesList = [],
    activeTab = 'terminal',
    onFileSelect,
    fileContents = {},
    isExpanded,
    onToggleExpand,
    viewMode,
    onViewModeChange,
    batchPlanTree = [],
    executionMode = 'review',
    isExecutionRunning = false,
    isPlanExecuting = false,
    onStartPlan,
    canStartPlan = false,
    onFindMoreIssuesForBatch,
    onChangeGoalForBatch,
    isFindMoreIssuesActionAvailableForBatch,
    isChangeGoalActionAvailableForBatch,
    onActiveTabChange,
    activeBatchId = null,
    activePackageIndex = null,
    activePlanVersion = null,
    currentStatus = null,
    executionStatus = null,
    forceAutoFollowSignal = 0,
    taskGoal = null,
    taskGoalLabel = null
}: WorkspacePanelProps) {
    const [currentTab, setCurrentTab] = useState<WorkspaceTab>(() => normalizeTab(activeTab));
    const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
    const [selectedPlanKey, setSelectedPlanKey] = useState<string | null>(null);
    const [planSelectionMode, setPlanSelectionMode] = useState<'auto' | 'manual'>('auto');
    const [pendingSystemFollowBatchId, setPendingSystemFollowBatchId] = useState<string | null>(null);
    const [systemBatchScrollTarget, setSystemBatchScrollTarget] = useState<string | null>(null);
    const [systemPlanScrollTarget, setSystemPlanScrollTarget] = useState<string | null>(null);
    const [isPlansSidebarOpen, setIsPlansSidebarOpen] = useState(true);
    const [batchGoalSelections, setBatchGoalSelections] = useState<Record<string, string>>({});
    const [plansGroupingMode, setPlansGroupingMode] = useState<PlansGroupingMode>(() => {
        if (typeof window === 'undefined') return 'batch';
        const stored = window.localStorage.getItem(PLANS_GROUPING_STORAGE_KEY);
        return stored === 'goal' ? 'goal' : 'batch';
    });
    const [expandedBatchIds, setExpandedBatchIds] = useState<Set<string>>(new Set());
    const batchButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const planButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const batchStripScrollerRef = useRef<HTMLDivElement | null>(null);
    const lastSystemFollowTargetBatchIdRef = useRef<string | null>(null);
    const lastAuthoritativeActivePlanKeyRef = useRef<string | null>(null);
    const lastForceAutoFollowSignalRef = useRef<number>(forceAutoFollowSignal);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(PLANS_GROUPING_STORAGE_KEY, plansGroupingMode);
    }, [plansGroupingMode]);

    const scrollPlanTabIntoView = (targetKey: string) => {
        const scrollTarget =
            planButtonRefs.current[targetKey]
            || (
                typeof document !== 'undefined'
                    ? document.querySelector(
                        `[data-plan-nav-key="${escapeAttributeValue(targetKey)}"]`
                    ) as HTMLElement | null
                    : null
            );
        if (!scrollTarget) return;
        const scrollContainer = findHorizontalScrollContainer(scrollTarget);
        if (scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const targetRect = scrollTarget.getBoundingClientRect();
            const targetStart = scrollContainer.scrollLeft + (targetRect.left - containerRect.left);
            const targetEnd = targetStart + targetRect.width;
            const visibleStart = scrollContainer.scrollLeft;
            const visibleEnd = visibleStart + scrollContainer.clientWidth;
            const padding = 8;
            if (targetStart < visibleStart) {
                scrollContainer.scrollTo({
                    left: Math.max(0, targetStart - padding),
                    behavior: 'auto'
                });
                return;
            }
            if (targetEnd > visibleEnd) {
                scrollContainer.scrollTo({
                    left: Math.max(0, targetEnd - scrollContainer.clientWidth + padding),
                    behavior: 'auto'
                });
                return;
            }
        }
        scrollTarget.scrollIntoView({
            block: 'nearest',
            inline: 'nearest'
        });
    };

    useEffect(() => {
        const nextTab = normalizeTab(activeTab);
        setCurrentTab((prev) => (prev === nextTab ? prev : nextTab));
    }, [activeTab]);
    const handleTabValueChange = (value: string) => {
        const nextTab = normalizeTab(value);
        setCurrentTab((prev) => (prev === nextTab ? prev : nextTab));
        onActiveTabChange?.(nextTab);
    };

    const allFilesDisplay = useMemo(
        () => fileChanges
            .map((change) => ({
                name: change.name,
                path: change.path,
                status: change.status,
                diff: change.diff,
                contentPreview: change.contentPreview
            }))
            .sort((a, b) => a.path.localeCompare(b.path)),
        [fileChanges]
    );

    const sortedBatches = useMemo(
        () => {
            const isTransitionalStatus = ['analyzing', 'in_progress'].includes(
                String(executionStatus || '').trim().toLowerCase()
            );
            return [...(Array.isArray(batchPlanTree) ? batchPlanTree : [])]
                .filter((entry) => entry && typeof entry.batchId === 'string' && entry.batchId.trim() !== '')
                .map((batch) => ({
                    ...batch,
                    plans: dedupeBatchPlans(batch)
                }))
                .filter((batch) => {
                    if (isTransitionalStatus) return true;
                    const status = String(batch.status || '').toLowerCase();
                    const bootstrapStatus = String(batch.bootstrapStatus || '').toLowerCase();
                    const hasNoPlans = batch.plans.length === 0;
                    const isBootstrapNotReadyOrEmpty = bootstrapStatus === 'not_ready' || bootstrapStatus === '';
                    if (status === 'pending' && hasNoPlans && isBootstrapNotReadyOrEmpty) {
                        return false;
                    }
                    return true;
                })
                .sort((a, b) => Number(a.rank || 0) - Number(b.rank || 0));
        },
        [batchPlanTree, executionStatus]
    );

    const totalPlanCount = useMemo(
        () => sortedBatches.reduce((sum, batch) => {
            const visibleCount = Array.isArray(batch.plans) ? batch.plans.length : 0;
            return sum + visibleCount;
        }, 0),
        [sortedBatches]
    );
    const goalGroupedBatches = useMemo(() => {
        const groups = new Map<string, {
            key: string;
            label: string;
            batches: AgentBatchPlanTreeBatch[];
        }>();
        for (const batch of sortedBatches) {
            const batchFallbackGoal = getBatchFallbackGoal(batch, taskGoal);
            const batchFallbackGoalLabel = getBatchFallbackGoalLabel(batch, taskGoal, taskGoalLabel);
            const planGroups = buildPlanGoalGroups(
                Array.isArray(batch.plans) ? batch.plans : [],
                batchFallbackGoal,
                batchFallbackGoalLabel
            );
            const groupsForBatch = planGroups.length > 0
                ? planGroups
                : [{
                    key: batchFallbackGoal ? batchFallbackGoal.toLowerCase() : 'ungrouped',
                    label: batchFallbackGoalLabel || 'Unassigned Goal',
                    plans: [] as AgentBatchPlanTreePlan[]
                }];
            for (const planGroup of groupsForBatch) {
                if (!groups.has(planGroup.key)) {
                    groups.set(planGroup.key, {
                        key: planGroup.key,
                        label: planGroup.label,
                        batches: []
                    });
                }
                groups.get(planGroup.key)!.batches.push({
                    ...batch,
                    plans: planGroup.plans
                });
            }
        }
        return Array.from(groups.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [sortedBatches, taskGoal, taskGoalLabel]);

    const selectedBatch = useMemo(
        () => findBatchByIdentity(sortedBatches, selectedBatchId),
        [sortedBatches, selectedBatchId]
    );

    const selectedPlan = useMemo(() => {
        if (!selectedBatch || !Array.isArray(selectedBatch.plans)) return null;
        return selectedBatch.plans.find((plan) => getPlanIdentityKey(plan) === selectedPlanKey) || null;
    }, [selectedBatch, selectedPlanKey]);
    const selectedBatchGoalGroups = useMemo(
        () => buildPlanGoalGroups(
            Array.isArray(selectedBatch?.plans) ? selectedBatch.plans : [],
            getBatchFallbackGoal(selectedBatch, taskGoal),
            getBatchFallbackGoalLabel(selectedBatch, taskGoal, taskGoalLabel)
        ),
        [selectedBatch, taskGoal, taskGoalLabel]
    );
    const selectedBatchContinuationGoalOptions = useMemo(
        () => Array.isArray(selectedBatch?.continuation?.goalOptions)
            ? selectedBatch.continuation.goalOptions.filter((option) => !!option && typeof option.value === 'string')
            : [],
        [selectedBatch]
    );

    useEffect(() => {
        const batchId = selectedBatch?.batchId;
        if (!batchId) return;
        const enabledGoal = selectedBatchContinuationGoalOptions.find((option) => option.disabled !== true)?.value || '';
        if (!enabledGoal) return;
        setBatchGoalSelections((prev) => {
            const currentValue = typeof prev[batchId] === 'string' ? prev[batchId] : '';
            const currentStillEnabled = selectedBatchContinuationGoalOptions.some((option) =>
                option.value === currentValue && option.disabled !== true
            );
            if (currentStillEnabled) return prev;
            return {
                ...prev,
                [batchId]: enabledGoal
            };
        });
    }, [selectedBatch?.batchId, selectedBatchContinuationGoalOptions]);
    const authoritativeActivePlanKey = useMemo(() => {
        if (!activeBatchId) return null;
        const activeBatch = findBatchByIdentity(sortedBatches, activeBatchId);
        const activePlan = getAuthoritativePlanForBatch(activeBatch, activePackageIndex, activePlanVersion);
        return activePlan ? getPlanIdentityKey(activePlan) : null;
    }, [activeBatchId, activePackageIndex, activePlanVersion, sortedBatches]);
    const normalizedExecutionStatus = String(executionStatus || '').trim().toLowerCase();
    const normalizedCurrentStatus = String(currentStatus || '').trim().toLowerCase();
    const workerActiveForPlanDisplay = isWorkerActiveForPlanDisplay({
        currentStatus,
        executionStatus,
        isExecutionRunning,
        isPlanExecuting
    });
    const shouldPreferReviewablePlan =
        normalizedCurrentStatus === 'pending_approval'
        || normalizedExecutionStatus === 'plan_pending_approval';
    const displayActivePlanNavKey = useMemo(() => {
        if (!activeBatchId) return null;
        const activeBatch = findBatchByIdentity(sortedBatches, activeBatchId);
        if (!activeBatch) return null;
        const displayActivePlan = getPreferredPlanForBatch(activeBatch, {
            authoritativePackageIndex: activePackageIndex,
            authoritativePlanVersion: activePlanVersion,
            preferReviewablePlan: shouldPreferReviewablePlan,
        });
        if (!displayActivePlan) return null;
        return `${activeBatch.batchId}::${getPlanIdentityKey(displayActivePlan)}`;
    }, [
        activeBatchId,
        activePackageIndex,
        activePlanVersion,
        shouldPreferReviewablePlan,
        sortedBatches,
    ]);
    const crossBatchFollowTarget = useMemo(() => {
        const workerDrivenStatuses = new Set([
            'analyzing',
            'cloning',
            'generating_plan',
            'regenerating_plan',
            'pending_approval',
            'plan_pending_approval',
            'waiting_clarification',
            'awaiting_input'
        ]);
        const isWorkerDrivenPlanning =
            workerDrivenStatuses.has(normalizedExecutionStatus)
            || workerDrivenStatuses.has(normalizedCurrentStatus);
        if (!isWorkerDrivenPlanning) return null;
        const currentBatchId = selectedBatchId || getDefaultBatchId(sortedBatches);
        const workerBatch = activeBatchId
            ? findBatchByIdentity(sortedBatches, activeBatchId)
            : null;
        const bootstrappingBatches = sortedBatches.filter((batch) => {
            const bootstrapStatus = String(batch.bootstrapStatus || '').trim().toLowerCase();
            return bootstrapStatus === 'bootstrapping';
        });
        const selectedBootstrappingBatch = currentBatchId
            ? findBatchByIdentity(bootstrappingBatches, currentBatchId)
            : null;
        const earliestBootstrappingBatch = bootstrappingBatches[0] || null;
        const activeWorkerPlan = getAuthoritativePlanForBatch(
            workerBatch,
            activePackageIndex,
            activePlanVersion
        );
        const workerBatchHasConcretePlans =
            !!workerBatch
            && Array.isArray(workerBatch.plans)
            && workerBatch.plans.some((plan) => isConcretePlan(plan));
        const workerBatchOwnsVisiblePlanLane =
            !!workerBatch && (!!activeWorkerPlan || workerBatchHasConcretePlans);

        if (
            workerBatchOwnsVisiblePlanLane
            && planSelectionMode === 'auto'
            && !!currentBatchId
            && !isSameBatchIdentity(currentBatchId, workerBatch?.batchId)
        ) {
            return workerBatch!.batchId;
        }
        if (workerBatch) {
            const bootstrapStatus = String(workerBatch.bootstrapStatus || '').trim().toLowerCase();
            const shouldFollowActiveWorkerBatch =
                planSelectionMode === 'auto'
                && String(workerBatch.status || '').trim().toLowerCase() === 'active'
                && !!currentBatchId
                && !isSameBatchIdentity(currentBatchId, workerBatch.batchId);
            const requiresCrossBatchFollow =
                bootstrapStatus === 'bootstrapping'
                || !Array.isArray(workerBatch.plans)
                || workerBatch.plans.length === 0
                || shouldFollowActiveWorkerBatch;
            if (requiresCrossBatchFollow && !workerBatchOwnsVisiblePlanLane) {
                if (!currentBatchId || !isSameBatchIdentity(currentBatchId, workerBatch.batchId)) {
                    return workerBatch.batchId;
                }
                return null;
            }
        }
        if (workerBatchOwnsVisiblePlanLane) return null;
        const stableBootstrappingTarget =
            selectedBootstrappingBatch?.batchId
            || earliestBootstrappingBatch?.batchId
            || null;
        if (!stableBootstrappingTarget || isSameBatchIdentity(stableBootstrappingTarget, currentBatchId)) {
            return null;
        }
        return stableBootstrappingTarget;
    }, [
        activeBatchId,
        activePackageIndex,
        activePlanVersion,
        normalizedCurrentStatus,
        normalizedExecutionStatus,
        planSelectionMode,
        selectedBatchId,
        sortedBatches
    ]);

    const selectBatchWithPreferredPlan = (batch: AgentBatchPlanTreeBatch | null) => {
        if (!batch?.batchId) {
            setSelectedBatchId(null);
            setSelectedPlanKey(null);
            setPlanSelectionMode('auto');
            setPendingSystemFollowBatchId(null);
            return;
        }
        setSelectedBatchId(batch.batchId);
        const defaultPlan = getPreferredPlanForBatch(batch);
        setSelectedPlanKey(defaultPlan ? getPlanIdentityKey(defaultPlan) : null);
        setPlanSelectionMode('auto');
        setPendingSystemFollowBatchId(null);
    };

    const selectPlanManually = (batchId: string, plan: AgentBatchPlanTreePlan) => {
        setSelectedBatchId(batchId);
        setSelectedPlanKey(getPlanIdentityKey(plan));
        setPlanSelectionMode('manual');
        setPendingSystemFollowBatchId(null);
    };

    const selectBatchManually = (batchId: string) => {
        setSelectedBatchId(batchId);
        setPlanSelectionMode('manual');
        setPendingSystemFollowBatchId(null);
    };

    useEffect(() => {
        if (!crossBatchFollowTarget) return;
        if (crossBatchFollowTarget === lastSystemFollowTargetBatchIdRef.current) return;
        lastSystemFollowTargetBatchIdRef.current = crossBatchFollowTarget;
        setPendingSystemFollowBatchId(crossBatchFollowTarget);
        setPlanSelectionMode('auto');
    }, [crossBatchFollowTarget]);

    useEffect(() => {
        if (forceAutoFollowSignal === lastForceAutoFollowSignalRef.current) return;
        lastForceAutoFollowSignalRef.current = forceAutoFollowSignal;
        setPlanSelectionMode('auto');
    }, [forceAutoFollowSignal]);

    useEffect(() => {
        const previousAuthoritativePlanKey = lastAuthoritativeActivePlanKeyRef.current;
        lastAuthoritativeActivePlanKeyRef.current = authoritativeActivePlanKey;
        if (!authoritativeActivePlanKey || !previousAuthoritativePlanKey) return;
        if (authoritativeActivePlanKey === previousAuthoritativePlanKey) return;
        if (!activeBatchId || !isSameBatchIdentity(selectedBatchId, activeBatchId)) return;
        if (planSelectionMode !== 'manual') return;
        setPlanSelectionMode('auto');
    }, [activeBatchId, authoritativeActivePlanKey, planSelectionMode, selectedBatchId]);

    useEffect(() => {
        if (sortedBatches.length === 0) {
            if (selectedBatchId !== null) setSelectedBatchId(null);
            if (selectedPlanKey !== null) setSelectedPlanKey(null);
            if (planSelectionMode !== 'auto') setPlanSelectionMode('auto');
            if (pendingSystemFollowBatchId !== null) setPendingSystemFollowBatchId(null);
            return;
        }

        const systemFollowBatch = pendingSystemFollowBatchId
            ? findBatchByIdentity(sortedBatches, pendingSystemFollowBatchId)
            : null;
        const batchExists = selectedBatchId
            ? !!findBatchByIdentity(sortedBatches, selectedBatchId)
            : false;
        const shouldApplySystemFollow = !!systemFollowBatch;
        const nextBatchId = shouldApplySystemFollow && systemFollowBatch
            ? systemFollowBatch.batchId
            : batchExists
                ? selectedBatchId
                : getDefaultBatchId(sortedBatches);
        if (nextBatchId && !isSameBatchIdentity(nextBatchId, selectedBatchId)) {
            setSelectedBatchId(nextBatchId);
            if (shouldApplySystemFollow) {
                setSystemBatchScrollTarget(nextBatchId);
            }
        }
        if (shouldApplySystemFollow && planSelectionMode !== 'auto') {
            setPlanSelectionMode('auto');
        }

        const resolvedBatch = findBatchByIdentity(sortedBatches, nextBatchId);
        const authoritativeSelection =
            planSelectionMode === 'auto' && isSameBatchIdentity(resolvedBatch?.batchId, activeBatchId)
                ? {
                    authoritativePackageIndex: activePackageIndex,
                    authoritativePlanVersion: activePlanVersion,
                    preferReviewablePlan: shouldPreferReviewablePlan,
                }
                : undefined;
        const nextPlan = shouldApplySystemFollow && resolvedBatch?.batchId === systemFollowBatch?.batchId
            ? (
                Array.isArray(resolvedBatch?.plans) && resolvedBatch.plans.length > 0
                    ? getPreferredPlanForBatch(resolvedBatch, authoritativeSelection)
                    : null
            )
            : getPreferredPlanForBatch(
                resolvedBatch,
                planSelectionMode === 'manual'
                    ? { preferredPlanKey: selectedPlanKey }
                    : authoritativeSelection
            );
        const nextPlanKey = nextPlan ? getPlanIdentityKey(nextPlan) : null;
        if (nextPlanKey !== selectedPlanKey) {
            setSelectedPlanKey(nextPlanKey);
            const shouldScrollToAutoFollowPlan =
                shouldApplySystemFollow
                || (
                    planSelectionMode === 'auto'
                    && isSameBatchIdentity(resolvedBatch?.batchId, activeBatchId)
                );
            if (shouldScrollToAutoFollowPlan && nextBatchId && nextPlanKey) {
                const targetKey = `${nextBatchId}::${nextPlanKey}`;
                setSystemPlanScrollTarget(targetKey);
                if (currentTab === 'plans') {
                    scrollPlanTabIntoView(targetKey);
                }
            }
        }
        if (nextPlanKey === null && selectedPlanKey !== null) {
            setSelectedPlanKey(null);
        }
        if (
            !shouldApplySystemFollow
            && planSelectionMode === 'manual'
            && selectedPlanKey
            && nextPlanKey !== selectedPlanKey
        ) {
            setPlanSelectionMode('auto');
        }
        if (shouldApplySystemFollow && nextPlanKey) {
            setPendingSystemFollowBatchId(null);
        }

        if (nextBatchId) {
            setExpandedBatchIds((prev) => {
                if (prev.has(nextBatchId)) return prev;
                const next = new Set(prev);
                next.add(nextBatchId);
                return next;
            });
        }
    }, [
        activeBatchId,
        activePackageIndex,
        activePlanVersion,
        shouldPreferReviewablePlan,
        pendingSystemFollowBatchId,
        planSelectionMode,
        selectedBatchId,
        selectedPlanKey,
        sortedBatches
    ]);

    useEffect(() => {
        if (currentTab !== 'plans' || !systemBatchScrollTarget) return;
        batchButtonRefs.current[systemBatchScrollTarget]?.scrollIntoView({
            block: 'nearest',
            inline: 'nearest'
        });
        setSystemBatchScrollTarget(null);
    }, [currentTab, systemBatchScrollTarget]);

    useEffect(() => {
        if (currentTab !== 'plans' || !systemPlanScrollTarget) return;
        scrollPlanTabIntoView(systemPlanScrollTarget);
        setSystemPlanScrollTarget(null);
    }, [currentTab, systemPlanScrollTarget]);

    useEffect(() => {
        if (currentTab === 'plans' && selectedBatchId && selectedPlanKey) {
            const targetKey = `${selectedBatchId}::${selectedPlanKey}`;
            scrollPlanTabIntoView(targetKey);
        }
    }, [currentTab, selectedBatchId, selectedPlanKey]);

    const resolveFileContent = (file: { path: string; diff?: string; contentPreview?: string }) => {
        const preview = typeof file.contentPreview === 'string' ? file.contentPreview.trim() : '';
        const hasInlineImagePreview = /^data:image\/[a-zA-Z0-9.+-]+;base64,/i.test(preview);
        if (hasInlineImagePreview) return preview;
        if (file.diff) return undefined;
        return file.contentPreview || fileContents[file.path];
    };

    const toggleBatchExpanded = (batchId: string) => {
        setExpandedBatchIds((prev) => {
            const next = new Set(prev);
            if (next.has(batchId)) {
                next.delete(batchId);
            } else {
                next.add(batchId);
            }
            return next;
        });
    };

    const renderStatusBadge = (status: string | undefined) => {
        const meta = getStatusMeta(status);
        const Icon = meta.icon;
        const normalized = String(status || '').trim().toLowerCase();
        const title = normalized === 'blocked'
            ? 'Blocked steps need review decision'
            : undefined;
        return (
            <span
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${meta.className}`}
                title={title}
            >
                <Icon className={`h-3 w-3 ${meta.spin ? 'animate-spin' : ''}`} />
                {meta.label}
            </span>
        );
    };

    const renderPlanDetails = (plan: AgentBatchPlanTreePlan | null, batch: AgentBatchPlanTreeBatch | null) => {
        const showStartPlanAction = canShowStartPlanAction({
            plan,
            batch,
            executionMode
        });
        const canInvokeStartPlanAction = canStartPlan && !isExecutionRunning;
        const showBatchContinuationActions = canShowBatchContinuationActions({
            batch,
            executionMode,
            isExecutionRunning,
            executionStatus
        });
        const continuationGoalOptions = Array.isArray(batch?.continuation?.goalOptions)
            ? batch.continuation.goalOptions.filter((option) => !!option && typeof option.value === 'string')
            : [];
        const selectableContinuationGoalOptions = continuationGoalOptions.filter(
            (option) => option.disabled !== true && String(option.value || '').trim() !== ''
        );
        const canRenderBatchContinuationControls =
            showBatchContinuationActions && selectableContinuationGoalOptions.length > 0;
        const selectedGoalValue = batch?.batchId
            ? (typeof batchGoalSelections[batch.batchId] === 'string' ? batchGoalSelections[batch.batchId] : '')
            : '';
        const continuationGoalLabel = typeof batch?.continuation?.currentGoalLabel === 'string' && batch.continuation.currentGoalLabel.trim() !== ''
            ? batch.continuation.currentGoalLabel.trim()
            : (
                typeof batch?.latestGoalLabel === 'string' && batch.latestGoalLabel.trim() !== ''
                    ? batch.latestGoalLabel.trim()
                    : null
            );
        const findMoreIssuesLabel = continuationGoalLabel
            ? `Find more ${continuationGoalLabel} issues`
            : 'Find more issues';
        const continuationNoticeLines = [
            batch?.continuation?.findMoreIssuesDisabledReason,
            batch?.continuation?.changeGoalDisabled === true
                ? batch?.continuation?.changeGoalDisabledReason
                : null
        ].filter((value, index, values): value is string =>
            typeof value === 'string'
            && value.trim() !== ''
            && values.indexOf(value) === index
        );
        const canInvokeChangeGoal = !!batch?.batchId
            && !!selectedGoalValue
            && continuationGoalOptions.some((option) => option.value === selectedGoalValue && option.disabled !== true)
            && batch?.continuation?.changeGoalDisabled !== true;
        const hasTokenizedFindMoreIssuesAction = !!batch?.batchId
            && (isFindMoreIssuesActionAvailableForBatch
                ? isFindMoreIssuesActionAvailableForBatch(batch.batchId)
                : true);
        const hasTokenizedChangeGoalAction = !!batch?.batchId
            && (isChangeGoalActionAvailableForBatch
                ? isChangeGoalActionAvailableForBatch(batch.batchId)
                : true);
        const renderBatchContinuationControls = (wrapperClassName = 'space-y-1.5') => (
            <div className={wrapperClassName}>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 max-w-full px-2 text-[11px] whitespace-nowrap"
                        disabled={batch?.continuation?.findMoreIssuesDisabled === true || !hasTokenizedFindMoreIssuesAction}
                        title={batch?.continuation?.findMoreIssuesDisabledReason || (!hasTokenizedFindMoreIssuesAction ? 'Refreshing actions...' : 'Find more issues in this batch')}
                        onClick={() => {
                            if (!batch?.batchId) return;
                            onFindMoreIssuesForBatch?.(batch.batchId);
                        }}
                    >
                        {findMoreIssuesLabel}
                    </Button>
                    <select
                        className="h-7 min-w-[7.5rem] max-w-[9rem] rounded-md border border-border bg-background px-2 text-[11px] text-foreground"
                        value={selectedGoalValue}
                        onChange={(event) => {
                            const nextGoal = event.target.value;
                            if (!batch?.batchId) return;
                            setBatchGoalSelections((prev) => ({
                                ...prev,
                                [batch.batchId]: nextGoal
                            }));
                        }}
                    >
                        {continuationGoalOptions.map((option) => (
                            <option
                                key={`batch-continuation-${batch?.batchId}-${option.value}`}
                                value={option.value}
                                disabled={option.disabled === true}
                            >
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] whitespace-nowrap"
                        disabled={!canInvokeChangeGoal || !hasTokenizedChangeGoalAction}
                        title={batch?.continuation?.changeGoalDisabledReason || (!hasTokenizedChangeGoalAction ? 'Refreshing actions...' : 'Find more issues in this batch under a different goal')}
                        onClick={() => {
                            if (!batch?.batchId || !selectedGoalValue) return;
                            onChangeGoalForBatch?.(batch.batchId, selectedGoalValue);
                        }}
                    >
                        Change goal
                    </Button>
                </div>
                {continuationNoticeLines.length > 0 ? (
                    <div className="space-y-1">
                        {continuationNoticeLines.map((message) => (
                            <div key={`continuation-note-${batch?.batchId}-${message}`} className="text-[11px] text-muted-foreground">
                                {message}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        );
        if (!plan) {
            const bootstrapMessage = replaceGenericBatchCopy(batch?.bootstrapDisplay?.message, batch?.rank);
            const placeholderMessage = (batch?.plans || []).length > 0
                ? 'Select a plan to view steps.'
                : (
                    bootstrapMessage
                    || (String(batch?.bootstrapStatus || '').toLowerCase() === 'skipped_empty'
                        ? 'No actionable improvements were found for this batch.'
                        : (String(batch?.bootstrapStatus || '').toLowerCase() === 'bootstrapping'
                            ? defaultGeneratingBatchCopy(batch?.rank)
                            : 'No concrete plans recorded for this batch yet.'))
                );
            const placeholderDetail = (batch?.plans || []).length > 0
                ? ''
                : (typeof batch?.bootstrapDisplay?.detail === 'string'
                    ? replaceGenericBatchCopy(batch.bootstrapDisplay.detail.trim(), batch?.rank)
                    : '');
            return (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                    <div className="flex flex-col items-center gap-1 text-center">
                        <span>{placeholderMessage}</span>
                        {placeholderDetail ? (
                            <span className="text-xs text-muted-foreground/80">{placeholderDetail}</span>
                        ) : null}
                    </div>
                    {canRenderBatchContinuationControls && batch?.batchId ? (
                        <div className="w-full max-w-md text-left">
                            {renderBatchContinuationControls()}
                        </div>
                    ) : null}
                </div>
            );
        }

        const isDetailActivePlan =
            plan.isActive === true
            || (
                !!batch?.batchId
                && `${batch.batchId}::${getPlanIdentityKey(plan)}` === displayActivePlanNavKey
            );
        // Do not override the plan's real status with 'in_progress' when the worker is
        // generating a *new* plan (e.g. after find_more_issues / continue_to_next_plan).
        // In that phase the existing plan card should keep its stored status (completed,
        // pending, etc.) — only the header badge shows 'Generating Plan…'.
        const normalizedExecStatusForBadge = String(executionStatus || '').trim().toLowerCase();
        const normalizedCurrentStatusForBadge = String(currentStatus || '').trim().toLowerCase();
        const isPlanGenerationPhase =
            PLAN_GENERATION_ONLY_STATUSES.has(normalizedExecStatusForBadge) ||
            PLAN_GENERATION_ONLY_STATUSES.has(normalizedCurrentStatusForBadge);
        const displayPlanStatus =
            isDetailActivePlan
            && workerActiveForPlanDisplay
            && (!isPlanGenerationPhase || isPlanExecuting === true)
            ? 'in_progress'
            : getLogicalPlanStatus(plan);

        return (
            <div className="h-full flex flex-col overflow-hidden">
                <div className="shrink-0 border-b border-border/70 px-3 py-2 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground truncate">
                                Batch {Math.max(1, Number(batch?.rank || 1))} / {getDisplayPlanLabel(batch, plan, {
                                    fallbackGoal: getBatchFallbackGoal(batch, taskGoal),
                                    fallbackGoalLabel: getBatchFallbackGoalLabel(batch, taskGoal, taskGoalLabel),
                                    forceGoalLabel: plansGroupingMode === 'batch',
                                    suppressGoalLabel: plansGroupingMode === 'goal'
                                })}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{plan.summary}</div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                            {renderStatusBadge(displayPlanStatus)}
                            {showStartPlanAction ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    className="h-6 px-2 text-[11px]"
                                    disabled={!canInvokeStartPlanAction}
                                    title={
                                        canInvokeStartPlanAction
                                            ? 'Start this plan'
                                            : 'Plan start is available when execution is idle.'
                                    }
                                    onClick={() => {
                                        if (!canInvokeStartPlanAction) return;
                                        if (batch?.batchId) {
                                            onStartPlan?.(batch.batchId, plan);
                                        }
                                    }}
                                >
                                    Start
                                </Button>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                        <span>{buildPlanProgressText(plan)}</span>
                        <div className="flex items-center gap-2">
                            {plan.prUrl ? (
                                <button
                                    type="button"
                                    className="text-primary hover:underline"
                                    onClick={() => window.open(String(plan.prUrl), '_blank', 'noopener,noreferrer')}
                                >
                                    View PR
                                </button>
                            ) : null}
                        </div>
                    </div>
                    {Array.isArray(batch?.scopePrefixes) && batch.scopePrefixes.length > 0 ? (
                        <div className="text-[11px] text-muted-foreground truncate" title={batch.scopePrefixes.join(', ')}>
                            Batch paths: {batch.scopePrefixes.join(', ')}
                        </div>
                    ) : null}
                    {typeof batch?.coverageSummary?.note === 'string' && batch.coverageSummary.note.trim() !== '' ? (
                        <div
                            className="text-[11px] text-amber-600 dark:text-amber-400"
                            title={batch.coverageSummary.note}
                        >
                            {batch.coverageSummary.note}
                        </div>
                    ) : null}
                    {canRenderBatchContinuationControls && batch?.batchId ? (
                        <div className="pt-1">
                            {renderBatchContinuationControls('space-y-1')}
                        </div>
                    ) : null}
                </div>

                <div className="flex-1 min-h-0 overflow-auto no-scrollbar">
                    {Array.isArray(plan.steps) && plan.steps.length > 0 ? (
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10 bg-background/95 border-b border-border/60">
                                <tr>
                                    <th className="text-left font-medium text-muted-foreground px-3 py-2">Step</th>
                                    <th className="text-left font-medium text-muted-foreground px-3 py-2 w-52">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {plan.steps.map((step: AgentBatchPlanTreeStep, rowIndex: number) => {
                                    const stepDisplayIndex = getPlanStepDisplayIndex(step, rowIndex);
                                    return (
                                        <tr key={getPlanStepKey(plan, step, rowIndex)} className="border-b border-border/40 last:border-b-0">
                                            <td className="px-3 py-2 text-foreground align-top">
                                                <div className="flex items-start gap-2">
                                                    <span className="text-muted-foreground shrink-0">{stepDisplayIndex}.</span>
                                                    <span>{step.title}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 align-top">{renderStatusBadge(step.status)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (plan.isGenerating || plan.isPreparing) ? (
                        <div className="p-4 text-xs text-muted-foreground">
                            {plan.isPreparing ? 'Preparing selected plan for execution...' : 'Plan is generating. Steps will appear shortly.'}
                        </div>
                    ) : (
                        <div className="p-4 text-xs text-muted-foreground">No step data available for this plan.</div>
                    )}
                </div>
            </div>
        );
    };

    const renderPlansGroupingControl = () => (
        <div className="inline-flex items-center rounded-md border border-border/70 bg-background/70 p-0.5">
            {(['batch', 'goal'] as PlansGroupingMode[]).map((mode) => (
                <button
                    key={mode}
                    type="button"
                    onClick={() => setPlansGroupingMode(mode)}
                    className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                        plansGroupingMode === mode
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground'
                    }`}
                >
                    {mode === 'batch' ? 'Batch' : 'Goal'}
                </button>
            ))}
        </div>
    );

    const renderBatchExplorerItem = (
        batch: AgentBatchPlanTreeBatch,
        options?: { suppressGoalHeader?: boolean }
    ) => {
        const isSelectedBatch = selectedBatchId === batch.batchId;
        const isExpandedBatch = expandedBatchIds.has(batch.batchId);
        const hasPlans = Array.isArray(batch.plans) && batch.plans.length > 0;
        const batchFallbackGoal = getBatchFallbackGoal(batch, taskGoal);
        const batchFallbackGoalLabel = getBatchFallbackGoalLabel(batch, taskGoal, taskGoalLabel);
        const showNestedGoalGroups = plansGroupingMode === 'goal' && options?.suppressGoalHeader !== true;
        const planGoalGroups = showNestedGoalGroups
            ? buildPlanGoalGroups(Array.isArray(batch.plans) ? batch.plans : [], batchFallbackGoal, batchFallbackGoalLabel)
            : [];

        return (
            <div key={`plans-tree-${batch.batchId}`} className="rounded-md border border-border/70 bg-background/50">
                <button
                    type="button"
                    ref={(node) => {
                        batchButtonRefs.current[batch.batchId] = node;
                    }}
                    disabled={executionMode === 'auto'}
                    onClick={() => {
                        if (executionMode === 'auto') return;
                        selectBatchManually(batch.batchId);
                        toggleBatchExpanded(batch.batchId);
                        if (typeof selectedPlanKey === 'string' && selectedPlanKey.trim() !== '') return;
                        selectBatchWithPreferredPlan(batch);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-2 py-2 text-left ${
                        isSelectedBatch ? 'bg-primary/10' : 'hover:bg-muted/40'
                    } ${executionMode === 'auto' ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                    <span className="flex items-center gap-1.5 min-w-0">
                        {isExpandedBatch ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                        <span className="text-xs font-medium truncate">
                            Batch {Math.max(1, Number(batch.rank || 1))}
                            {String(batch.status || '').toLowerCase() === 'active' ? ' (active)' : ''}
                        </span>
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                        {Math.max(
                            Math.max(0, Number(batch.totalPackages || 0)),
                            Array.isArray(batch.plans) ? batch.plans.length : 0
                        )} plans
                    </span>
                </button>

                {isExpandedBatch && (
                    <div className="px-2 pb-2 space-y-1">
                        {hasPlans ? (
                            (showNestedGoalGroups
                                ? planGoalGroups
                                : [{ key: 'batch', label: '', plans: batch.plans }]).map((group) => (
                                <div key={`plans-tree-goal-${batch.batchId}-${group.key}`} className="space-y-1">
                                    {showNestedGoalGroups ? (
                                        <div className="ml-5 px-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                            {group.label}
                                        </div>
                                    ) : null}
                                    {group.plans.map((plan) => {
                                        const isSelectedByKey = isSelectedBatch && getPlanIdentityKey(plan) === selectedPlanKey;
                                        const isActivePlan =
                                            `${batch.batchId}::${getPlanIdentityKey(plan)}` === displayActivePlanNavKey;
                                        const planLabel = getDisplayPlanLabel(batch, plan, {
                                            fallbackGoal: batchFallbackGoal,
                                            fallbackGoalLabel: batchFallbackGoalLabel,
                                            forceGoalLabel: plansGroupingMode === 'batch',
                                            suppressGoalLabel: plansGroupingMode === 'goal',
                                            active: isActivePlan
                                        });
                                        return (
                                            <button
                                                key={`plans-tree-plan-${batch.batchId}-${getPlanIdentityKey(plan)}`}
                                                type="button"
                                                data-plan-nav-key={`${batch.batchId}::${getPlanIdentityKey(plan)}`}
                                                ref={(node) => {
                                                    planButtonRefs.current[
                                                        `${batch.batchId}::${getPlanIdentityKey(plan)}`
                                                    ] = node;
                                                }}
                                                disabled={executionMode === 'auto'}
                                                onClick={() => {
                                                    if (executionMode === 'auto') return;
                                                    selectPlanManually(batch.batchId, plan);
                                                }}
                                                className={`w-full rounded-md border px-2 py-1.5 text-left ${
                                                    isSelectedByKey
                                                        ? 'border-primary/50 bg-primary/10'
                                                        : 'border-border bg-background hover:bg-muted/40'
                                                } ${executionMode === 'auto' ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                style={{ marginLeft: '1.25rem' }}
                                            >
                                                <div className="text-xs text-foreground truncate">
                                                    {planLabel}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            ))
                        ) : (
                            <div className="text-[11px] text-muted-foreground px-1 py-1">No plans for this batch yet.</div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderPlansExplorerContent = () => {
        if (plansGroupingMode === 'goal') {
            return goalGroupedBatches.map((group) => (
                <div key={`plans-goal-group-${group.key}`} className="space-y-1">
                    <div className="px-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        {group.label}
                    </div>
                    {group.batches.map((batch) => renderBatchExplorerItem(batch, { suppressGoalHeader: true }))}
                </div>
            ));
        }
        return sortedBatches.map((batch) => renderBatchExplorerItem(batch));
    };

    const renderPlansInNormalView = () => (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="shrink-0 border-b border-border/70 px-2 py-1.5">
                <HorizontalScrollStrip
                    scrollerRef={batchStripScrollerRef}
                    scrollerTestId="plans-batch-strip"
                >
                    {sortedBatches.map((batch) => {
                        const isSelected = selectedBatchId === batch.batchId;
                        const isActive = String(batch.status || '').toLowerCase() === 'active';
                        return (
                            <button
                                key={`plans-strip-${batch.batchId}`}
                                type="button"
                                ref={(node) => {
                                    batchButtonRefs.current[batch.batchId] = node;
                                }}
                                disabled={executionMode === 'auto'}
                                onClick={() => selectBatchWithPreferredPlan(batch)}
                                className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] transition-colors ${
                                    isSelected
                                        ? 'border-primary/50 bg-primary/10 text-primary'
                                        : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/40'
                                } ${executionMode === 'auto' ? 'opacity-70 cursor-not-allowed' : ''}`}
                                title={`Batch ${Math.max(1, Number(batch.rank || 1))}`}
                            >
                                {isActive ? <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-primary/80 align-middle" /> : null}
                                Batch {Math.max(1, Number(batch.rank || 1))}
                            </button>
                        );
                    })}
                </HorizontalScrollStrip>
            </div>

            <div className="shrink-0 border-b border-border/70 px-2 py-1.5">
                <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0 py-0.5">
                        {(selectedBatch?.plans || []).length > 0 ? (
                            <div className="space-y-2">
                                {(plansGroupingMode === 'goal'
                                    ? selectedBatchGoalGroups
                                    : [{ key: 'batch', label: '', plans: selectedBatch?.plans || [] }]).map((group) => (
                                    <div key={`plans-normal-goal-${selectedBatch?.batchId}-${group.key}`} className="space-y-1">
                                        {plansGroupingMode === 'goal' ? (
                                            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                                {group.label}
                                            </div>
                                        ) : null}
                                        <HorizontalScrollStrip>
                                            {group.plans.map((plan) => {
                                                const isSelected = getPlanIdentityKey(plan) === selectedPlanKey;
                                                const isActivePlan =
                                                    !!selectedBatch?.batchId
                                                    && `${selectedBatch.batchId}::${getPlanIdentityKey(plan)}` === displayActivePlanNavKey;
                                                const planLabel = getDisplayPlanLabel(selectedBatch || null, plan, {
                                                    fallbackGoal: getBatchFallbackGoal(selectedBatch, taskGoal),
                                                    fallbackGoalLabel: getBatchFallbackGoalLabel(selectedBatch, taskGoal, taskGoalLabel),
                                                    forceGoalLabel: plansGroupingMode === 'batch',
                                                    suppressGoalLabel: plansGroupingMode === 'goal',
                                                    active: isActivePlan
                                                });
                                                return (
                                                    <button
                                                        key={`plans-normal-strip-${selectedBatch?.batchId}-${getPlanIdentityKey(plan)}`}
                                                        type="button"
                                                        data-plan-nav-key={
                                                            selectedBatch?.batchId
                                                                ? `${selectedBatch.batchId}::${getPlanIdentityKey(plan)}`
                                                                : undefined
                                                        }
                                                        ref={(node) => {
                                                            if (!selectedBatch?.batchId) return;
                                                            planButtonRefs.current[
                                                                `${selectedBatch.batchId}::${getPlanIdentityKey(plan)}`
                                                            ] = node;
                                                        }}
                                                        disabled={executionMode === 'auto'}
                                                        onClick={() => {
                                                            if (!selectedBatch?.batchId) return;
                                                            selectPlanManually(selectedBatch.batchId, plan);
                                                        }}
                                                        className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] transition-colors ${
                                                            isSelected
                                                                ? 'border-primary/50 bg-primary/10 text-primary'
                                                                : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/40'
                                                        } ${executionMode === 'auto' ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                    >
                                                        {isActivePlan ? <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-primary/80 align-middle" /> : null}
                                                        {planLabel}
                                                    </button>
                                                );
                                            })}
                                        </HorizontalScrollStrip>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div />
                        )}
                    </div>
                    {renderPlansGroupingControl()}
                    {onToggleExpand ? (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={onToggleExpand}
                            title="Expand Plans Panel"
                        >
                            <Expand className="h-3.5 w-3.5" />
                        </Button>
                    ) : null}
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden">{renderPlanDetails(selectedPlan, selectedBatch)}</div>
        </div>
    );

    const renderPlansInExpandedView = () => (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="shrink-0 border-b border-border/70 px-2 py-1 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Button
                        variant={isPlansSidebarOpen ? 'secondary' : 'ghost'}
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setIsPlansSidebarOpen((prev) => !prev)}
                        title="Toggle Plans Explorer"
                    >
                        <PanelLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs text-muted-foreground">Plans Explorer</span>
                    {renderPlansGroupingControl()}
                </div>
                {onToggleExpand ? (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={onToggleExpand}
                        title="Collapse Plans Panel"
                    >
                        <Minimize2 className="h-3.5 w-3.5" />
                    </Button>
                ) : null}
            </div>

            <div className="flex-1 min-h-0 flex overflow-hidden">
                {isPlansSidebarOpen && (
                    <div className="w-[280px] min-w-[240px] max-w-[360px] border-r border-border/70 overflow-y-auto no-scrollbar p-2 space-y-1 bg-muted/5">
                        {renderPlansExplorerContent()}
                    </div>
                )}

                <div className="flex-1 min-w-0 overflow-hidden">{renderPlanDetails(selectedPlan, selectedBatch)}</div>
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col overflow-hidden w-full max-w-full border-0 bg-transparent">
            <Tabs value={currentTab} onValueChange={handleTabValueChange} className="flex-1 flex flex-col h-full">
                <div>
                    <TabsList className="w-full justify-start h-9 bg-transparent p-0 gap-2">
                        <TabsTrigger
                            value="files"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs px-3 h-7 rounded-md"
                        >
                            <FileCode className="h-3.5 w-3.5 mr-1.5" />
                            Files
                            {fileChanges.length > 0 && (
                                <span className="ml-1.5 h-4 min-w-[1rem] px-1 text-[10px] rounded-full bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                                    {fileChanges.length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="plans"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs px-3 h-7 rounded-md"
                        >
                            <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                            Plans
                            {totalPlanCount > 0 && (
                                <span className="ml-1.5 h-4 min-w-[1rem] px-1 text-[10px] rounded-full bg-secondary text-secondary-foreground inline-flex items-center justify-center">
                                    {totalPlanCount}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="terminal"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs px-3 h-7 rounded-md"
                        >
                            <Terminal className="h-3.5 w-3.5 mr-1.5" />
                            Terminal
                        </TabsTrigger>
                    </TabsList>
                </div>

                <div className="flex-1 overflow-hidden relative">
                    <TabsContent value="files" className="h-full m-0 p-0 overflow-hidden text-sm">
                        {allFilesDisplay.length > 0 ? (
                            <CodePanel
                                onFileSelect={onFileSelect}
                                files={allFilesDisplay.map((f) => ({
                                    name: f.name,
                                    path: f.path,
                                    status: f.status,
                                    diff: f.diff,
                                    content: resolveFileContent(f),
                                    contentPreview: f.contentPreview,
                                    language: f.path.split('.').pop(),
                                }))}
                                isExpanded={isExpanded}
                                onToggleExpand={onToggleExpand}
                                viewMode={viewMode}
                                onViewModeChange={onViewModeChange}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                {modifiedFilesSyncWarning ? (
                                    <>
                                        <AlertTriangle className="h-8 w-8 mb-2 opacity-40" />
                                        <div className="max-w-xs space-y-1 text-center">
                                            <p className="text-xs font-medium text-foreground">
                                                File changes were applied, but diff sync degraded.
                                            </p>
                                            <p className="text-[11px] text-muted-foreground">
                                                {typeof modifiedFilesSyncWarning.message === 'string' && modifiedFilesSyncWarning.message.trim() !== ''
                                                    ? modifiedFilesSyncWarning.message
                                                    : 'Fallback file paths were saved, but rich diffs could not be loaded yet. Refresh or retry to load more detail.'}
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <FileCode className="h-8 w-8 mb-2 opacity-20" />
                                        <span className="text-xs">
                                            {filesList.length === 0 ? 'Loading files...' : 'No files found.'}
                                        </span>
                                    </>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="plans" className="h-full m-0 p-0 overflow-hidden text-sm bg-background">
                        {sortedBatches.length > 0 ? (
                            isExpanded ? renderPlansInExpandedView() : renderPlansInNormalView()
                        ) : (
                            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                                No plans recorded yet.
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="terminal" className="h-full m-0 p-0 relative bg-zinc-950 overflow-hidden">
                        <div className="h-full p-4">
                            <TerminalOutput
                                command="agent execution logs"
                                output={terminalOutput}
                                status="running"
                                expandable={false}
                                defaultExpanded={true}
                                autoScroll
                                className="h-full flex flex-col"
                                bodyClassName="flex-1 min-h-0"
                            />
                        </div>
                    </TabsContent>
                </div>
            </Tabs>
        </div>
    );
}




